import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { User, Mail, Phone, Lock, ShieldCheck, ArrowRight, RefreshCw } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

import api from '../services/api';
import { useToast } from '../context/ToastContext';

export default function Register() {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        phone: '',
        otp: ''
    });
    const [step, setStep] = useState(1); // 1: Details, 2: OTP
    const [loading, setLoading] = useState(false);
    const { setAuth, googleLogin } = useAuth();
    const { isPortal, tenant } = useTenant();
    const navigate = useNavigate();
    const toast = useToast();

    const validatePassword = (password) => {
        if (password.length < 8) return 'Password must be at least 8 characters long';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
        if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
        if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least one special character';
        return null;
    };

    const handleSendOTP = async (e) => {
        e.preventDefault();
        
        // Client-side phone validation
        if (!formData.phone || formData.phone.trim() === '') {
            return toast.error('Phone number is required');
        }
        const phoneRegex = /^(?:\+?91)?[6-9]\d{9}$/;
        if (!phoneRegex.test(formData.phone.trim())) {
            return toast.error('Please enter a valid 10-digit mobile number (e.g. 9876543210)');
        }

        // Client-side password validation
        const passwordError = validatePassword(formData.password);
        if (passwordError) {
            return toast.error(passwordError);
        }

        setLoading(true);
        try {
            await api.post('/api/auth/request-otp', { email: formData.email, type: 'signup' });
            toast.success('Verification code sent to your email');
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to send OTP');
        } finally {
            setLoading(false);
        }
    };

    const navigateBasedOnRole = (user, token) => {
        if (isPortal && user.role !== 'super_admin' && (user.slug || user.custom_frontend_url)) {
            const host = window.location.host.replace('www.', '');
            const isStaffOrAdmin = ['restaurant_admin', 'kitchen_staff', 'delivery_partner'].includes(user.role);
            const domain = (isStaffOrAdmin ? null : user.custom_frontend_url) || `http://${user.slug}.${host}`;
            let path = '/';
            if (user.role === 'restaurant_admin') path = '/admin';
            else if (user.role === 'kitchen_staff') path = '/kitchen';
            else if (user.role === 'delivery_partner') path = '/delivery';
            
            window.location.href = `${domain}/login?token=${token}&redirect=${path}`;
            return;
        }

        if (user.role === 'super_admin') {
            navigate('/super-admin');
        } else if (user.role === 'restaurant_admin') {
            navigate('/admin');
        } else if (user.role === 'kitchen_staff') {
            navigate('/kitchen');
        } else if (user.role === 'delivery_partner') {
            navigate('/delivery');
        } else {
            navigate('/');
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await api.post('/api/auth/register', formData);
            toast.success('Account created successfully!');
            setAuth(res.data.token, res.data.user);
            navigateBasedOnRole(res.data.user, res.data.token);
        } catch (err) {
            // Check for specific validation errors from express-validator
            const errors = err.response?.data?.errors;
            if (errors && Array.isArray(errors) && errors.length > 0) {
                toast.error(errors[0].msg);
            } else {
                toast.error(err.response?.data?.message || 'Registration failed. Please check your details.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (credential) => {
        setLoading(true);
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const returnOrigin = urlParams.get('return_origin');
            const res = await googleLogin(credential, returnOrigin, tenant?.id);
            
            if (returnOrigin) {
                window.location.href = `${returnOrigin}/login?token=${res.token}`;
                return;
            }

            toast.success('Account created with Google!');
            if (isPortal && res.user && res.user.slug) {
                const host = window.location.host.replace('www.', '');
                window.location.href = `http://${res.user.slug}.${host}/login?token=${res.token}&redirect=/`;
            } else {
                navigate('/');
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Google registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-3xl shadow-2xl border border-gray-100">
                <div className="text-center">
                    <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                        {step === 1 ? 'Create Account' : 'Verify Email'}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {step === 1 
                            ? 'Join us and start ordering today!' 
                            : `We've sent a code to ${formData.email}`}
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={step === 1 ? handleSendOTP : handleRegister}>
                    {step === 1 ? (
                        <div className="space-y-4">
                            <div className="relative">
                                <User className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    required
                                    className="appearance-none block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all"
                                    placeholder="Full Name"
                                    value={formData.fullName}
                                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                />
                            </div>
                            <div className="relative">
                                <Mail className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                <input
                                    type="email"
                                    required
                                    className="appearance-none block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all"
                                    placeholder="Email address"
                                    value={formData.email}
                                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                                />
                            </div>
                             <div className="relative">
                                 <Phone className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                 <input
                                     type="tel"
                                     required
                                     className="appearance-none block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all"
                                     placeholder="Phone Number"
                                     value={formData.phone}
                                     onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                 />
                             </div>
                            <div className="relative space-y-3">
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                    <input
                                        type="password"
                                        required
                                        className="appearance-none block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all"
                                        placeholder="Secure Password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                    />
                                </div>
                                
                                {/* Password Requirement Checklist */}
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-2">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Security Requirements</p>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                        <div className={`text-[11px] flex items-center ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-1 h-1 rounded-full mr-1.5 ${formData.password.length >= 8 ? 'bg-green-600' : 'bg-gray-300'}`} />
                                            8+ characters
                                        </div>
                                        <div className={`text-[11px] flex items-center ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-1 h-1 rounded-full mr-1.5 ${/[A-Z]/.test(formData.password) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                            Uppercase
                                        </div>
                                        <div className={`text-[11px] flex items-center ${/[a-z]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-1 h-1 rounded-full mr-1.5 ${/[a-z]/.test(formData.password) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                            Lowercase
                                        </div>
                                        <div className={`text-[11px] flex items-center ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-1 h-1 rounded-full mr-1.5 ${/[0-9]/.test(formData.password) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                            Number
                                        </div>
                                        <div className={`text-[11px] flex items-center ${/[^A-Za-z0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                                            <div className={`w-1 h-1 rounded-full mr-1.5 ${/[^A-Za-z0-9]/.test(formData.password) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                            Special Char
                                        </div>
                                    </div>
                                    
                                    {/* Strength Bar */}
                                    <div className="mt-3 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full transition-all duration-300 ${
                                                [
                                                    formData.password.length >= 8,
                                                    /[A-Z]/.test(formData.password),
                                                    /[a-z]/.test(formData.password),
                                                    /[0-9]/.test(formData.password),
                                                    /[^A-Za-z0-9]/.test(formData.password)
                                                ].filter(Boolean).length <= 2 ? 'bg-red-500 w-1/3' : 
                                                [
                                                    formData.password.length >= 8,
                                                    /[A-Z]/.test(formData.password),
                                                    /[a-z]/.test(formData.password),
                                                    /[0-9]/.test(formData.password),
                                                    /[^A-Za-z0-9]/.test(formData.password)
                                                ].filter(Boolean).length <= 4 ? 'bg-yellow-500 w-2/3' : 'bg-green-500 w-full'
                                            }`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="relative">
                                <ShieldCheck className="absolute left-3 top-4 h-6 w-6 text-primary-500" />
                                <input
                                    type="text"
                                    required
                                    maxLength="6"
                                    className="appearance-none block w-full px-12 py-4 text-center text-2xl font-bold tracking-[0.5em] border border-gray-300 placeholder-gray-400 text-gray-900 rounded-xl focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-all"
                                    placeholder="000000"
                                    value={formData.otp}
                                    onChange={(e) => setFormData({...formData, otp: e.target.value.replace(/\D/g, '')})}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="text-sm text-gray-500 hover:text-primary-600 transition-colors flex items-center justify-center w-full"
                            >
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Change email or details
                            </button>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all shadow-lg shadow-primary-200"
                    >
                        {loading ? (
                            <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <span>{step === 1 ? 'Send Verification Code' : 'Verify & Create Account'}</span>
                                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </>
                        )}
                    </button>
                </form>

                {step === 1 && (
                    <>
                        <div className="relative my-8">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500 font-medium">Or sign up with</span>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            {(!isPortal && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const platformHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                                            ? 'http://localhost:5173'
                                            : 'https://www.aaharamcloud.com';
                                        const centralLoginUrl = `${platformHost}/register?return_origin=${encodeURIComponent(window.location.origin)}`;
                                        window.location.href = centralLoginUrl;
                                    }}
                                    className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-gray-300 rounded-full text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all shadow-sm hover:border-gray-400"
                                >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                                    </svg>
                                    <span>Sign up with Google</span>
                                </button>
                            ) : (
                                <GoogleLogin
                                    onSuccess={credentialResponse => {
                                        handleGoogleSuccess(credentialResponse.credential);
                                    }}
                                    onError={() => {
                                        toast.error('Google registration failed');
                                    }}
                                    shape="pill"
                                    theme="outline"
                                    size="large"
                                />
                            )}
                        </div>
                    </>
                )}

                <div className="text-center">
                    <p className="text-sm text-gray-600">
                        Already have an account?{' '}
                        <Link to="/login" className="font-bold text-primary-600 hover:text-primary-500 transition-colors">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}